import { createContext, useCallback, useContext, useRef, type ReactNode } from 'react';

type EnqueueOperation = <T>(operation: () => Promise<T>) => Promise<T>;

const FormOperationContext = createContext<EnqueueOperation | undefined>(undefined);

export function FormOperationProvider({ children }: { children: ReactNode }) {
  const pending = useRef<Promise<unknown>>(Promise.resolve());
  const enqueue = useCallback<EnqueueOperation>((operation) => {
    const result = pending.current.then(operation);
    // Recover the queue while leaving the operation's rejection visible to its caller.
    pending.current = result.catch(() => {});
    return result;
  }, []);

  return <FormOperationContext.Provider value={enqueue}>{children}</FormOperationContext.Provider>;
}

export function useFormOperationQueue() {
  const enqueue = useContext(FormOperationContext);
  if (!enqueue) throw new Error('Form operations require a FormOperationProvider.');
  return enqueue;
}
